const PAGE_SIZE = 100;

type PageResult = { data?: unknown; error?: unknown };
type FetchPage = (page: number, size: number) => Promise<PageResult>;

// Count CSV records, not lines: quoted weather fields can contain newlines.
function parsePage(csv: string) {
  const records: string[] = [];
  let quoted = false;
  let start = 0;
  for (let i = 0; i < csv.length; i++) {
    if (csv[i] === '"') {
      if (quoted && csv[i + 1] === '"') i++;
      else quoted = !quoted;
    } else if (!quoted && (csv[i] === '\r' || csv[i] === '\n')) {
      records.push(csv.slice(start, i));
      if (csv[i] === '\r' && csv[i + 1] === '\n') i++;
      start = i + 1;
    }
  }
  if (quoted) throw new Error('Invalid flight summary CSV');
  if (start < csv.length) records.push(csv.slice(start));
  const header = records.shift()?.replace(/^\uFEFF/, '');
  if (!header?.startsWith('Date,')) throw new Error('Invalid flight summary CSV header');
  return { header, records };
}

function isEmptyPage(error: unknown, page: number) {
  const response = error as { status?: number; data?: unknown } | undefined;
  if (response?.status !== 400) return false;
  try {
    const body = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
    return body?.message === `No flight summaries found for page: ${page}`;
  } catch {
    return false;
  }
}

export async function downloadFlightSummaries(fetchPage: FetchPage, signal?: AbortSignal): Promise<string> {
  const chunks: string[] = [];
  let header: string | undefined;
  for (let page = 0; ; page++) {
    if (signal?.aborted) throw new Error('Flight summary download cancelled');
    const result = await fetchPage(page, PAGE_SIZE);
    if (signal?.aborted) throw new Error('Flight summary download cancelled');
    if (result.error) {
      // The current API returns 400 for an empty page, including after an exact
      // multiple of PAGE_SIZE. Other failures must never produce a partial file.
      if (page > 0 && isEmptyPage(result.error, page)) break;
      throw result.error;
    }
    if (typeof result.data !== 'string') throw new Error('Invalid flight summary response');
    const parsed = parsePage(result.data);
    if (header === undefined) {
      header = parsed.header;
      chunks.push(header + '\r\n');
    } else if (header !== parsed.header) {
      throw new Error('Flight summary CSV headers changed during download');
    }
    if (parsed.records.length) chunks.push(parsed.records.join('\r\n') + '\r\n');
    if (parsed.records.length < PAGE_SIZE) break;
  }
  return chunks.join('');
}
